import { useState } from "react";
import { useReveal } from "../hooks";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xbdvwrqa";

const DIVISIONS = ["Telecom / B2B sales", "HVAC / Energy efficiency", "Other industry"];
const CAPACITIES = ["Under 50 customers / month", "50–200 customers / month", "200–1,000 customers / month", "1,000+ customers / month"];
const REVENUES = ["Pre-revenue", "Under $500K / year", "$500K – $2M / year", "$2M – $10M / year", "$10M+ / year"];
const TIMELINES = ["As soon as possible", "Within 1–3 months", "Within 3–6 months", "Just exploring"];

export const QualifyWizard = () => {
  const [ref, inView] = useReveal(0.15);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    division: "", company_name: "", website: "", fulfillment_capacity: "",
    revenue_range: "", timeline: "", goal: "", contact_name: "", email: "", phone: "",
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };

  const next = () => {
    if (step === 0 && !form.division) return setError("Pick the division that fits you best.");
    if (step === 1 && (!form.company_name || !form.fulfillment_capacity)) return setError("Company name and fulfillment capacity are required.");
    if (step === 2 && (!form.revenue_range || !form.timeline)) return setError("Revenue range and timeline are required.");
    setError(""); setStep(step + 1);
  };

  const submit = async () => {
    if (!form.contact_name || !/^\S+@\S+\.\S+$/.test(form.email)) return setError("Your name and a valid email are required.");
    setSending(true); setError("");
    try {
      const payload = {
        _subject: `New Qualify submission — ${form.company_name || form.contact_name}`,
        _replyto: form.email,
        Division: form.division,
        Company: form.company_name,
        Website: form.website || "—",
        "Fulfillment capacity": form.fulfillment_capacity,
        "Revenue range": form.revenue_range,
        Timeline: form.timeline,
        Goal: form.goal || "—",
        Name: form.contact_name,
        Email: form.email,
        Phone: form.phone || "—",
      };
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Formspree error");
      setDone(true);
    } catch {
      setError("Something went wrong sending your answers. Please try again or email info@swiftmarketing.ca.");
    } finally {
      setSending(false);
    }
  };

  const Pills = ({ options, field }) => (
    <div className="wizard-pills">
      {options.map((o) => (
        <button key={o} type="button" className={`wizard-pill ${form[field] === o ? "sel" : ""}`}
          onClick={() => set(field, o)} data-testid={`wizard-pill-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {o}
          <svg className="tick" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
        </button>
      ))}
    </div>
  );

  const STEPS = [
    { label: "Step 1 / 4 — Your market", title: "Which division fits your business?", body: <Pills options={DIVISIONS} field="division" /> },
    {
      label: "Step 2 / 4 — Your company", title: "Tell us about your operation", body: (
        <>
          <div className="wizard-field"><label>Company name *</label>
            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Energy Inc." data-testid="wizard-company-input" /></div>
          <div className="wizard-field"><label>Website (optional)</label>
            <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="acme.ca" data-testid="wizard-website-input" /></div>
          <div className="wizard-field"><label>How many new customers can you fulfill? *</label>
            <select value={form.fulfillment_capacity} onChange={(e) => set("fulfillment_capacity", e.target.value)} data-testid="wizard-capacity-select">
              <option value="">Select capacity…</option>
              {CAPACITIES.map((c) => <option key={c}>{c}</option>)}
            </select></div>
        </>
      ),
    },
    {
      label: "Step 3 / 4 — Your goals", title: "Where are you today, and how fast do you want to move?", body: (
        <>
          <div className="wizard-field"><label>Current annual revenue *</label>
            <select value={form.revenue_range} onChange={(e) => set("revenue_range", e.target.value)} data-testid="wizard-revenue-select">
              <option value="">Select range…</option>
              {REVENUES.map((r) => <option key={r}>{r}</option>)}
            </select></div>
          <div className="wizard-field"><label>When do you want reps in the field? *</label>
            <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="wizard-timeline-select">
              <option value="">Select timeline…</option>
              {TIMELINES.map((t) => <option key={t}>{t}</option>)}
            </select></div>
          <div className="wizard-field"><label>Main growth goal (optional)</label>
            <textarea rows="3" value={form.goal} onChange={(e) => set("goal", e.target.value)} placeholder="e.g. 500 new residential customers in Quebec this year" data-testid="wizard-goal-input" /></div>
        </>
      ),
    },
    {
      label: "Step 4 / 4 — Contact", title: "Where should we send your assessment?", body: (
        <>
          <div className="wizard-field"><label>Your name *</label>
            <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Jean Tremblay" data-testid="wizard-name-input" /></div>
          <div className="wizard-field"><label>Work email *</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jean@acme.ca" data-testid="wizard-email-input" /></div>
          <div className="wizard-field"><label>Phone (optional)</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 438 000-0000" data-testid="wizard-phone-input" /></div>
        </>
      ),
    },
  ];

  return (
    <section className="sec wizard" id="qualify" data-testid="qualify-section" ref={ref}>
      <div className="container">
        <div className={`reveal ${inView ? "in" : ""}`} style={{ textAlign: "center" }}>
          <span className="eyebrow">Fit assessment</span>
          <h2 className="h-display">Check if you qualify</h2>
          <p className="lead" style={{ margin: "0 auto" }}>
            Four quick questions. If the economics don't make sense for you, we'll tell you — no obligations.
          </p>
        </div>
        <div className={`wizard-card reveal d1 ${inView ? "in" : ""}`}>
          {done ? (
            <div className="wizard-success" data-testid="wizard-success">
              <div className="check">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <h3>Application received</h3>
              <p className="lead" style={{ margin: "0 auto" }}>
                Thanks {form.contact_name.split(" ")[0]} — our team will review your answers and reach
                out at <b>{form.email}</b> within one business day.
              </p>
            </div>
          ) : (
            <>
              <div className="wizard-progress">
                {STEPS.map((_, i) => <i key={i} className={i <= step ? "on" : ""} />)}
              </div>
              <div className="wizard-step-label">{STEPS[step].label}</div>
              <h3>{STEPS[step].title}</h3>
              {STEPS[step].body}
              {error && <div className="wizard-error" data-testid="wizard-error">{error}</div>}
              <div className="wizard-nav">
                {step > 0 ? (
                  <button className="wizard-back" onClick={() => { setStep(step - 1); setError(""); }} data-testid="wizard-back-btn">← Back</button>
                ) : <span />}
                {step < 3 ? (
                  <button className="btn" onClick={next} data-testid="wizard-next-btn">Continue →</button>
                ) : (
                  <button className="btn" onClick={submit} disabled={sending} data-testid="wizard-submit-btn">
                    {sending ? "Sending…" : "Submit application"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
